"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataMapper = void 0;
const tiny_typed_emitter_1 = require("tiny-typed-emitter");
const diff_1 = __importDefault(require("./utils/diff"));
const applyPatches_1 = __importDefault(require("./utils/applyPatches"));
const MapResultNode_1 = require("./internal/MapResultNode");
const DataProxy_1 = require("./internal/DataProxy");
const Immer_1 = require("./internal/Immer");
const path_1 = require("./utils/path");
const resolveConnectTarget_1 = require("./internal/resolveConnectTarget");
/**
 * @fires DataMapper#node:created
 * @fires DataMapper#node:updated
 * @fires DataMapper#node:destroyed
 *
 * @fires DataMapper#patched
 * @fires DataMapper#initialized
 */
class DataMapper extends tiny_typed_emitter_1.TypedEmitter {
    /* istanbul ignore next */
    constructor(opts) {
        super();
        this.rules = opts.rules || {};
        this.main = typeof opts.main === 'string' || !opts.main ? this.rules[opts.main || 'default'] : opts.main;
        /* istanbul ignore next */
        if (!this.main)
            throw new Error('You must provide main rule');
        this.resultNodesByPath = new Map();
        this.resultNodesBySrcValue = new WeakMap();
    }
    /**
     * 删除现有的数据节点，会重置 src 和 result
     */
    dispose() {
        if (this.resultNode) {
            this.resultNode.dispose();
            this.resultNode = undefined;
        }
        this.result = undefined;
        this.src = undefined;
    }
    initializeWith(src, options) {
        this.dispose();
        this.applyPatches([{ op: 'set', path: '', value: src }], options);
    }
    getRule(r) {
        if (!r)
            return null;
        if (typeof r === 'string')
            return this.rules[r];
        return r;
    }
    /**
     * 在给定一个路径的时候，如果需要知道它对应哪个 node（或者挂在哪个 node 下面），可以使用这个方法去取。
     *
     * 由于这个路径指向的数据，可能 **不直接指向** node，而是 `node.src` 里面某个深层属性。
     * 因此，特别需要注意返回的 `relativePathToNode` 是否为空
     *
     * @param target 形如 `/foo/bar` 的字符串路径
     */
    /* istanbul ignore next */
    findNodeToPath(target) {
        return (0, resolveConnectTarget_1.resolveConnectTarget)(this, target);
    }
    /**
     * 获取一个 DataProxy，以便无痛的像操作 mutable 对象的方式玩转数据
     *
     * 这个东西有点像 createImmer，区别是会自动异步的 finish() + 提交修改 + 再次创建 Immer
     *
     * 配合 Vue2 插件，可以无痛的与 Vue2 双向绑定结合。
     *
     * 详细见 README
     */
    createDataProxy() {
        return new DataProxy_1.DataProxy(this);
    }
    /**
     * 创建一个类似 Immer Draft 的东西，用户可以在 `proxy` (也就是draft) 上随便修改，
     * 然后通过 `finish()` 就可以拿到 patches
     *
     * ```js
     * // 创建一个 Immer
     * const immer = dataMapper.createImmer('')
     *
     * // ... 直接在 proxy 对数据随便修改
     * immer.proxy.foo = 'bar'
     *
     * // 完成修改，生成 patches，并提交修改
     * const patches = immer.finish()
     * dataMapper.applyPatches(patches)
     * ```
     */
    createImmer(path = '') {
        let { src } = this;
        (0, path_1.toPathParts)(path).forEach((x) => {
            if (src)
                src = src[x];
        });
        return (0, Immer_1.createImmer)(src, path);
    }
    modify(processor) {
        const immer = this.createImmer('');
        const finalize = () => {
            const patches = immer.finish();
            if (patches.length)
                this.applyPatches(patches);
        };
        let isAsync = false;
        try {
            const ans = processor(immer.proxy);
            isAsync = !!ans && ('then' in ans) && typeof ans.then === 'function';
            if (isAsync)
                return ans.finally(finalize);
        }
        finally {
            if (!isAsync)
                finalize();
        }
    }
    applyPatches(patches, options) {
        const oldSrc = this.src;
        const newSrc = (0, applyPatches_1.default)(oldSrc, patches, options);
        const isInitializing = !oldSrc;
        let { resultNode } = this;
        if (!resultNode) {
            // first initialize
            resultNode = new MapResultNode_1.MapResultNode(this, this.main, '', newSrc);
            this.resultNode = resultNode;
        }
        else {
            /* istanbul ignore next */
            if (oldSrc === newSrc)
                return; // nothing changed
            resultNode.setSrc(newSrc);
        }
        const { result } = resultNode;
        this.result = result;
        this.src = newSrc;
        // console.group('applyPatches');
        // console.dir({ ctx: this, patches, options });
        // console.groupEnd();
        if (isInitializing)
            this.emit('initialized', { src: newSrc, result });
        else
            this.emit('patched', { src: newSrc, result, patches, oldSrc });
    }
    diff(src) {
        const patches = (0, diff_1.default)(this.src, src, {
            getArrayKey: (_, path) => { var _a; return (_a = this.resultNodesByPath.get(path)) === null || _a === void 0 ? void 0 : _a.rule.srcItemsKey; },
            isAtomic: path => { var _a; return !!((_a = this.resultNodesByPath.get(path)) === null || _a === void 0 ? void 0 : _a.rule.isAtomic); },
        });
        return patches;
    }
    /**
     * 这个方法相当于先执行 `diff` 然后 `applyPatches`
     *
     * @param src
     */
    migrateTo(src, options) {
        const patches = this.diff(src);
        this.applyPatches(patches, options);
    }
}
exports.DataMapper = DataMapper;
